import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import { DownloadOutlined, ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import dayjs, { type Dayjs } from "dayjs";
import {
  exportPaymentOrders,
  fetchPaymentOrders,
  type PaymentOrderListItem,
  type PaymentOrderListQuery,
} from "@/api/client";
import { countryLabel } from "@/utils/countryLabel";
import {
  notifyStatusLabel,
  paymentStatusColor,
  paymentStatusLabel,
} from "@/utils/paymentLabels";

type FilterValues = {
  merchantOrder?: string;
  platformOrderId?: string;
  paymentStatus?: string;
  dateRange?: [Dayjs, Dayjs];
};

function defaultDateRange(): [Dayjs, Dayjs] {
  const end = dayjs().endOf("day");
  const start = end.subtract(1, "month").startOf("day");
  return [start, end];
}

function toQuery(
  values: FilterValues,
  page: number,
  pageSize: number
): PaymentOrderListQuery {
  const [start, end] = values.dateRange ?? defaultDateRange();
  return {
    page,
    pageSize,
    merchantOrder: values.merchantOrder?.trim() || undefined,
    platformOrderId: values.platformOrderId?.trim() || undefined,
    paymentStatus: values.paymentStatus || undefined,
    startDate: start.format("YYYY-MM-DD"),
    endDate: end.format("YYYY-MM-DD"),
  };
}

export default function PaymentOrdersPage() {
  const [form] = Form.useForm<FilterValues>();
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [list, setList] = useState<PaymentOrderListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const load = useCallback(
    async (nextPage = page, nextPageSize = pageSize) => {
      const values = form.getFieldsValue();
      setLoading(true);
      setError(null);
      try {
        const data = await fetchPaymentOrders(toQuery(values, nextPage, nextPageSize));
        setList(data.list);
        setTotal(data.total);
        setPage(data.page);
        setPageSize(data.pageSize);
      } catch (err) {
        setError(err instanceof Error ? err.message : "加载支付订单失败");
        setList([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    },
    [form, page, pageSize]
  );

  useEffect(() => {
    void load(1, pageSize);
    // 仅首次进入拉取；后续由查询/分页触发
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSearch = async () => {
    setPage(1);
    await load(1, pageSize);
  };

  const onReset = async () => {
    form.setFieldsValue({
      merchantOrder: undefined,
      platformOrderId: undefined,
      paymentStatus: undefined,
      dateRange: defaultDateRange(),
    });
    setPage(1);
    await load(1, pageSize);
  };

  const onExport = async () => {
    setExporting(true);
    try {
      const values = form.getFieldsValue();
      const query = toQuery(values, 1, pageSize);
      const blob = await exportPaymentOrders({
        merchantOrder: query.merchantOrder,
        platformOrderId: query.platformOrderId,
        paymentStatus: query.paymentStatus,
        startDate: query.startDate,
        endDate: query.endDate,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `payment-orders-${dayjs().format("YYYYMMDDHHmmss")}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      message.success("导出成功");
    } catch (err) {
      message.error(err instanceof Error ? err.message : "导出失败");
    } finally {
      setExporting(false);
    }
  };

  const onTableChange = (pagination: TablePaginationConfig) => {
    const nextPage = pagination.current ?? 1;
    const nextSize = pagination.pageSize ?? pageSize;
    setPage(nextPage);
    setPageSize(nextSize);
    void load(nextPage, nextSize);
  };

  const columns: ColumnsType<PaymentOrderListItem> = [
    {
      title: "平台订单",
      dataIndex: "platformOrderId",
      width: 220,
      ellipsis: true,
    },
    {
      title: "商户",
      dataIndex: "merchantId",
      width: 240,
      ellipsis: false,
    },
    {
      title: "国家",
      dataIndex: "country",
      width: 90,
      render: (v: string) => countryLabel(v || "RU"),
    },
    {
      title: "商户单号",
      dataIndex: "merchantOrder",
      width: 320,
      ellipsis: false,
    },
    {
      title: "金额",
      dataIndex: "price",
      width: 100,
      align: "right",
    },
    {
      title: "币种",
      dataIndex: "currency",
      width: 72,
    },
    {
      title: "美金",
      dataIndex: "usdAmount",
      width: 100,
      align: "right",
      render: (v: string | null) => v ?? "—",
    },
    {
      title: "汇率",
      dataIndex: "exchangeRate",
      width: 90,
      align: "right",
      render: (v: string | null) => v ?? "—",
    },
    {
      title: "支付状态",
      dataIndex: "paymentStatus",
      width: 100,
      render: (v: string) => (
        <Tag color={paymentStatusColor(v)}>{paymentStatusLabel(v)}</Tag>
      ),
    },
    {
      title: "发货状态",
      dataIndex: "notifyStatus",
      width: 100,
      render: (v: string) => (
        <span className={v === "PENDING" || !v ? "payment-orders__ship-pending" : undefined}>
          {notifyStatusLabel(v)}
        </span>
      ),
    },
    {
      title: "渠道",
      dataIndex: "channelId",
      width: 80,
      ellipsis: true,
    },
    {
      title: "创建时间",
      dataIndex: "createdAt",
      width: 170,
      render: (v: string | null) => v ?? "—",
    },
    {
      title: "支付时间",
      dataIndex: "paidAt",
      width: 170,
      render: (v: string | null) => v ?? "—",
    },
    {
      title: "通知时间",
      dataIndex: "notifiedAt",
      width: 170,
      render: (v: string | null) => v ?? "—",
    },
  ];

  return (
    <div className="payment-orders-page">
      <Typography.Title level={4} className="payment-orders-page__title">
        支付订单
      </Typography.Title>

      {error ? (
        <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />
      ) : null}

      <Card className="payment-orders-page__card" bordered={false}>
        <Form
          form={form}
          layout="horizontal"
          initialValues={{ dateRange: defaultDateRange() }}
          onFinish={() => void onSearch()}
          className="payment-orders-page__filters"
        >
          <Row gutter={[16, 12]}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item name="merchantOrder" label="商户订单号">
                <Input allowClear placeholder="请输入商户订单号" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item name="platformOrderId" label="平台订单号">
                <Input allowClear placeholder="请输入平台订单号" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item name="dateRange" label="日期">
                <DatePicker.RangePicker style={{ width: "100%" }} allowClear={false} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item name="paymentStatus" label="支付状态">
                <Select
                  allowClear
                  placeholder="全部"
                  options={[
                    { value: "UNPAID", label: "未支付" },
                    { value: "PAID", label: "已支付" },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Space wrap className="payment-orders-page__actions">
                <Button type="primary" htmlType="submit" icon={<SearchOutlined />} loading={loading}>
                  查询
                </Button>
                <Button icon={<ReloadOutlined />} onClick={() => void onReset()}>
                  重置
                </Button>
              </Space>
            </Col>
          </Row>
        </Form>

        <div className="payment-orders-page__toolbar">
          <Button icon={<DownloadOutlined />} loading={exporting} onClick={() => void onExport()}>
            导出
          </Button>
        </div>

        <Table<PaymentOrderListItem>
          rowKey="platformOrderId"
          size="middle"
          loading={loading}
          columns={columns}
          dataSource={list}
          scroll={{ x: 2100 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: (t) => `共 ${t} 条`,
          }}
          onChange={onTableChange}
        />
      </Card>
    </div>
  );
}
